import { ArrowDown, ArrowUp, Minus, IndianRupee, Truck, Package, Receipt, MapPin } from "lucide-react";

/**
 * Sprint 78 polish — Cost recalculation preview.
 *
 * Replaces the older "X updates to Y" one-liner with a transparent
 * side-by-side breakdown showing exactly which factor changed the price:
 *   • Distance (km)
 *   • Chargeable weight (kg)
 *   • Base / slab charge
 *   • Zone multiplier
 *   • GST
 *   • COD fee
 *   • Surge
 *   • Final total
 *
 * Hides rows where the value didn't move so the customer doesn't drown
 * in noise. Each row is colour-coded:  ↑ red  ·  ↓ green  ·  — slate.
 *
 * Props:
 *   preview: { old_price, new_price, price_changed, diff,
 *              reason, message, adjustment_type, difference }
 *              // server response
 */
export default function CostRecalcPreview({ preview }) {
  if (!preview) return null;

  const op = preview.old_price || {};
  const np = preview.new_price || {};
  const oldTotal = num(op.final_price);
  const newTotal = num(np.final_price);
  const delta = round2(newTotal - oldTotal);
  const isCharge = delta > 0;
  const isRefund = delta < 0;
  const isSame = delta === 0;

  // Rows we want to surface — only show if they actually moved.
  const rows = [
    { key: "distance_km",       label: "Distance",          unit: "km",  icon: MapPin },
    { key: "actual_weight_kg",  label: "Actual weight",     unit: "kg",  icon: Package },
    { key: "volumetric_weight_kg", label: "Volumetric weight", unit: "kg", icon: Package },
    { key: "chargeable_weight_kg", label: "Chargeable weight", unit: "kg", icon: Package },
    { key: "weight_slab",       label: "Weight slab",       unit: "",    icon: Package, text: true },
    { key: "zone_label",        label: "Zone",              unit: "",    icon: Truck, text: true },
    { key: "zone_multiplier",   label: "Zone multiplier",   unit: "x",   icon: Truck },
    { key: "delivery_charge",   label: "Base delivery",     unit: "₹",   icon: Truck },
    { key: "weight_charge_display", label: "Weight charge", unit: "₹",   icon: Package },
    { key: "longhaul_surcharge", label: "Long-haul surcharge", unit: "₹", icon: Truck },
    { key: "pre_surge_price",   label: "Subtotal (pre-surge)", unit: "₹", icon: Receipt },
    { key: "surge_amount",      label: "Surge",             unit: "₹",   icon: Truck },
    { key: "cod_fee",           label: "COD fee",           unit: "₹",   icon: Receipt },
    { key: "gst",               label: "GST",               unit: "₹",   icon: Receipt },
  ];

  const movedRows = rows
    .map((r) => {
      const oldV = op[r.key];
      const newV = np[r.key];
      if (oldV === undefined && newV === undefined) return null;
      if (String(oldV ?? "") === String(newV ?? "")) return null;
      return { ...r, oldV, newV };
    })
    .filter(Boolean);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="cost-recalc-preview">
      {/* Header banner */}
      <div className={`px-4 py-3 flex items-center justify-between gap-3 ${
        isSame ? "bg-slate-50" : isCharge ? "bg-rose-50" : "bg-emerald-50"
      }`}>
        <div className="flex items-center gap-2">
          {isSame ? <Minus className="h-5 w-5 text-slate-500" /> :
           isCharge ? <ArrowUp className="h-5 w-5 text-rose-600" /> :
                      <ArrowDown className="h-5 w-5 text-emerald-600" />}
          <div>
            <p className={`text-sm font-bold ${
              isSame ? "text-slate-700" : isCharge ? "text-rose-700" : "text-emerald-700"
            }`} data-testid="cost-recalc-headline">
              {preview.message || (isSame ? "No price change" :
               isCharge ? "Additional amount payable" :
                          "Refund amount — credited to wallet")}
            </p>
            <p className="text-[10px] text-slate-500">Nothing is saved or charged yet.</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">
            {isSame ? "No delta" : isCharge ? "Extra to pay" : "Refund"}
          </p>
          <p className={`text-2xl font-extrabold font-['Outfit'] flex items-center justify-end gap-0.5 ${
            isSame ? "text-slate-700" : isCharge ? "text-rose-700" : "text-emerald-700"
          }`} data-testid="cost-recalc-delta">
            <IndianRupee className="h-5 w-5" />
            {Math.abs(delta).toFixed(2)}
          </p>
        </div>
      </div>

      {/* Reason for change (when provided) */}
      {preview.reason && (
        <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 text-xs text-amber-900" data-testid="cost-recalc-reason">
          <span className="font-bold">Reason: </span>
          {preview.reason}
        </div>
      )}

      {/* Old vs new totals */}
      <div className="px-4 py-3 grid grid-cols-3 gap-3 border-b border-slate-100 text-center">
        <Total label="Was" amount={oldTotal} dim testId="cost-recalc-old-total" />
        <div className="flex items-center justify-center text-slate-300 text-xl">→</div>
        <Total label="Will be" amount={newTotal} highlight={!isSame} tone={isCharge ? "rose" : isRefund ? "emerald" : "slate"} testId="cost-recalc-new-total" />
      </div>

      {/* Per-factor breakdown */}
      {movedRows.length === 0 ? (
        <p className="px-4 py-3 text-xs text-slate-400 text-center italic">
          No pricing factors changed.
        </p>
      ) : (
        <ul className="divide-y divide-slate-50" data-testid="cost-recalc-rows">
          {movedRows.map((r) => (
            <Row key={r.key} {...r} />
          ))}
        </ul>
      )}

      {/* Footer note */}
      <div className="px-4 py-2 bg-slate-50 text-[10px] text-slate-500 leading-relaxed">
        Recomputed with the same engine used at booking: weight slab × zone × distance + GST.
        {isCharge && " Customer must pay before next critical stage (admin override available)."}
        {isRefund && " Refund auto-credits to wallet on save (wallet_transactions logged)."}
      </div>
    </div>
  );
}

function Row({ label, unit, icon: Icon, oldV, newV, text }) {
  const dir = text ? "neutral" : compare(oldV, newV);
  const dirClass = dir === "up" ? "text-rose-600" : dir === "down" ? "text-emerald-600" : "text-slate-500";
  const Arrow = dir === "up" ? ArrowUp : dir === "down" ? ArrowDown : Minus;
  return (
    <li className="px-4 py-2.5 flex items-center justify-between gap-3 text-sm">
      <div className="flex items-center gap-2 min-w-0">
        <Icon className="h-3.5 w-3.5 text-slate-400 shrink-0" />
        <span className="text-slate-700 truncate">{label}</span>
      </div>
      <div className="flex items-center gap-2 shrink-0 font-mono text-xs">
        <span className="line-through opacity-50 text-slate-500">
          {formatVal(oldV, unit)}
        </span>
        <Arrow className={`h-3 w-3 ${dirClass}`} />
        <span className={`font-bold ${dirClass}`}>
          {formatVal(newV, unit)}
        </span>
      </div>
    </li>
  );
}

function Total({ label, amount, dim, highlight, tone = "slate", testId }) {
  const toneClass = tone === "rose" ? "text-rose-700" : tone === "emerald" ? "text-emerald-700" : "text-slate-900";
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">{label}</p>
      <p className={`font-['Outfit'] font-bold flex items-center justify-center ${
        highlight ? `text-xl ${toneClass}` : `text-base ${dim ? "text-slate-400 line-through opacity-70" : "text-slate-900"}`
      }`} data-testid={testId}>
        <IndianRupee className="h-3.5 w-3.5" />{(amount ?? 0).toFixed(2)}
      </p>
    </div>
  );
}

// ───────── helpers ─────────
function num(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}
function round2(n) {
  return Math.round(n * 100) / 100;
}
function compare(a, b) {
  const x = num(a), y = num(b);
  if (y > x) return "up";
  if (y < x) return "down";
  return "neutral";
}
function formatVal(v, unit) {
  if (v === null || v === undefined || v === "") return "—";
  if (unit === "₹") return `₹${num(v).toFixed(2)}`;
  if (unit === "x") return `${num(v).toFixed(2)}×`;
  if (unit === "kg") return `${num(v).toFixed(2)} kg`;
  if (unit === "km") return `${num(v).toFixed(2)} km`;
  return String(v);
}
